import React, { useRef } from "react";
import { Paper, Typography, Box, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { BiBrain } from "react-icons/bi";
import { RiStarLine } from "react-icons/ri";
import InfoIcon from "@material-ui/icons/Info";
import PrintIcon from "@material-ui/icons/Print";
import { useReactToPrint } from "react-to-print";
import AISummaryPrintable from "./AISummaryPrintable";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
    borderRadius: 12,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "rgba(123, 44, 191, 0.04)",
    borderRadius: 8,
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginRight: theme.spacing(2),
  },
  title: {
    color: "#7B2CBF",
    fontWeight: 500,
    flex: 1,
  },
  content: {
    padding: theme.spacing(2),
    "& h1": {
      fontSize: "1.8rem",
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      fontWeight: 500,
      color: theme.palette.text.primary,
      borderBottom: "2px solid #7B2CBF",
      paddingBottom: theme.spacing(1),
    },
    "& h2": {
      fontSize: "1.3rem",
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      fontWeight: 500,
      color: "#7B2CBF",
    },
    "& h3": {
      fontSize: "1.1rem",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    "& p": {
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
      color: theme.palette.text.secondary,
      "&.critical-warning": {
        backgroundColor: "rgba(244, 67, 54, 0.08)",
        border: "1px solid rgba(244, 67, 54, 0.3)",
        borderRadius: 8,
        padding: theme.spacing(1.5),
        color: "#d32f2f",
        fontWeight: 500,
      },
    },
    "& ul": {
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      color: theme.palette.text.secondary,
      listStyleType: "disc",
    },
    "& ol": {
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      color: theme.palette.text.secondary,
    },
    "& li": {
      marginBottom: theme.spacing(1),
    },
    "& strong": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    "& em": {
      fontStyle: "italic",
    },
    "& code": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      padding: "2px 6px",
      borderRadius: 4,
      fontFamily: "monospace",
      fontSize: "0.9em",
    },
    "& pre": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      padding: theme.spacing(2),
      borderRadius: 8,
      overflow: "auto",
      marginBottom: theme.spacing(2),
    },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: theme.spacing(2),
      border: "1px solid rgba(0, 0, 0, 0.12)",
    },
    "& th": {
      backgroundColor: "rgba(123, 44, 191, 0.08)",
      padding: theme.spacing(1.5),
      textAlign: "left",
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: "2px solid rgba(123, 44, 191, 0.3)",
    },
    "& td": {
      padding: theme.spacing(1.5),
      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
      color: theme.palette.text.secondary,
    },
    "& tr:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
    "& hr": {
      border: "none",
      borderTop: "1px solid rgba(0, 0, 0, 0.12)",
      margin: theme.spacing(3, 0),
    },
    "& blockquote": {
      borderLeft: "4px solid #7B2CBF",
      paddingLeft: theme.spacing(2),
      marginLeft: 0,
      marginBottom: theme.spacing(2),
      color: theme.palette.text.secondary,
      fontStyle: "italic",
    },
  },
  emptyState: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
  disclaimer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: 8,
    border: "1px solid rgba(0, 0, 0, 0.08)",
  },
  disclaimerIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  disclaimerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  criticalWarning: {
    backgroundColor: "rgba(244, 67, 54, 0.08)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    "& strong": {
      color: "#d32f2f",
    },
  },
}));

/**
 * Detects if text contains Markdown syntax
 * @param {string} text - Text to analyze
 * @returns {boolean} - True if Markdown is detected
 */
export const isMarkdown = (text) => {
  if (!text) return false;

  // Regex patterns to detect common Markdown elements
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers (# ## ###)
    /\*\*[^*]+\*\*/g, // Bold (**text**)
    /\*[^*]+\*/g, // Italic (*text*)
    /^\s*[-*+]\s/m, // Unordered lists (-, *, +)
    /^\s*\d+\.\s/m, // Ordered lists (1. 2. 3.)
    /\[.+\]\(.+\)/g, // Links [text](url)
    /^\|.*\|$/m, // Tables (|col1|col2|)
    /^---+$/m, // Horizontal rules (---)
    /^>\s/m, // Blockquotes (>)
    /`[^`]+`/g, // Inline code (`code`)
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
};

/**
 * Escapes HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
export const escapeHtml = (text) => {
  const htmlEntities = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return text.replace(/[&<>"']/g, (char) => htmlEntities[char]);
};

/**
 * Processes inline Markdown syntax (bold, italic, code, etc.)
 * @param {string} text - Text to process
 * @returns {string} - HTML with inline elements
 */
const processInlineMarkdown = (text) => {
  return (
    text
      // Bold (**text**)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      // Italic (*text* or _text_)
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/_([^_]+)_/g, "<em>$1</em>")
      // Inline code (`code`)
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      // Links [text](url)
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
  );
};

/**
 * Converts Markdown text to HTML
 * @param {string} text - Markdown text
 * @returns {string} - HTML string
 */
export const formatMarkdown = (text) => {
  if (!text) return "";

  const lines = text.split("\n");
  const html = [];
  let inList = false;
  let inOrderedList = false;
  let inTable = false;
  let inCodeBlock = false;
  let inBlockquote = false;
  let tableHeaders = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines in certain contexts
    if (!trimmedLine && !inCodeBlock) {
      if (inList) {
        html.push("</ul>");
        inList = false;
      }
      if (inOrderedList) {
        html.push("</ol>");
        inOrderedList = false;
      }
      if (inTable) {
        html.push("</tbody></table>");
        inTable = false;
        tableHeaders = [];
      }
      if (inBlockquote) {
        html.push("</blockquote>");
        inBlockquote = false;
      }
      continue;
    }

    // Code blocks (```)
    if (trimmedLine.startsWith("```")) {
      if (inCodeBlock) {
        html.push("</code></pre>");
        inCodeBlock = false;
      } else {
        html.push("<pre><code>");
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      html.push(escapeHtml(line));
      continue;
    }

    // Headers (# ## ### etc.)
    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const headerText = processInlineMarkdown(headerMatch[2]);
      html.push(`<h${level}>${headerText}</h${level}>`);
      continue;
    }

    // Horizontal rule (---)
    if (/^---+$/.test(trimmedLine)) {
      html.push("<hr />");
      continue;
    }

    // Tables
    if (trimmedLine.startsWith("|") && trimmedLine.endsWith("|")) {
      const cells = trimmedLine
        .slice(1, -1)
        .split("|")
        .map((cell) => cell.trim());

      // Check if it's a separator line (|---|---|)
      if (cells.every((cell) => /^-+$/.test(cell))) {
        if (!inTable) {
          html.push("<table><thead><tr>");
          tableHeaders.forEach((header) => {
            html.push(`<th>${processInlineMarkdown(header)}</th>`);
          });
          html.push("</tr></thead><tbody>");
          inTable = true;
        }
        continue;
      }

      // Store headers or add table rows
      if (!inTable && tableHeaders.length === 0) {
        tableHeaders = cells;
        continue;
      }

      if (inTable) {
        html.push("<tr>");
        cells.forEach((cell) => {
          html.push(`<td>${processInlineMarkdown(cell)}</td>`);
        });
        html.push("</tr>");
      }
      continue;
    }

    // Close table if we're no longer in table context
    if (inTable && !trimmedLine.startsWith("|")) {
      html.push("</tbody></table>");
      inTable = false;
      tableHeaders = [];
    }

    // Blockquotes (>)
    if (trimmedLine.startsWith(">")) {
      if (!inBlockquote) {
        html.push("<blockquote>");
        inBlockquote = true;
      }
      const quoteText = processInlineMarkdown(trimmedLine.substring(1).trim());
      html.push(`<p>${quoteText}</p>`);
      continue;
    }

    if (inBlockquote && !trimmedLine.startsWith(">")) {
      html.push("</blockquote>");
      inBlockquote = false;
    }

    // Unordered lists (-, *, +)
    const unorderedListMatch = trimmedLine.match(/^[-*+]\s+(.+)$/);
    if (unorderedListMatch) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }
      const listItemText = processInlineMarkdown(unorderedListMatch[1]);
      html.push(`<li>${listItemText}</li>`);
      continue;
    }

    // Ordered lists (1. 2. 3.)
    const orderedListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
    if (orderedListMatch) {
      if (!inOrderedList) {
        html.push("<ol>");
        inOrderedList = true;
      }
      const listItemText = processInlineMarkdown(orderedListMatch[2]);
      html.push(`<li>${listItemText}</li>`);
      continue;
    }

    // Close lists if we're no longer in list context
    if (inList && !trimmedLine.match(/^[-*+]\s/)) {
      html.push("</ul>");
      inList = false;
    }
    if (inOrderedList && !trimmedLine.match(/^\d+\.\s/)) {
      html.push("</ol>");
      inOrderedList = false;
    }

    // Regular paragraphs
    if (trimmedLine) {
      const paragraphText = processInlineMarkdown(trimmedLine);

      // Add special styling for critical warnings
      if (
        trimmedLine.includes("⚠️") ||
        trimmedLine.includes("CRÍTICO") ||
        trimmedLine.includes("URGENTE")
      ) {
        html.push(`<p class="critical-warning">${paragraphText}</p>`);
      } else {
        html.push(`<p>${paragraphText}</p>`);
      }
    }
  }

  // Close any open tags
  if (inList) html.push("</ul>");
  if (inOrderedList) html.push("</ol>");
  if (inTable) html.push("</tbody></table>");
  if (inCodeBlock) html.push("</code></pre>");
  if (inBlockquote) html.push("</blockquote>");

  return html.join("\n");
};

const AISummary = ({ summary, patientInfo = {} }) => {
  const classes = useStyles();
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "ai-health-summary",
    pageStyle: "@page { margin: 15mm }",
  });

  if (!summary) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.emptyState}>
          <div
            className={classes.iconContainer}
            style={{ justifyContent: "center", marginBottom: 16 }}
          >
            <RiStarLine style={{ fontSize: 16, color: "#7B2CBF" }} />
            <BiBrain style={{ fontSize: 24, color: "#7B2CBF" }} />
            <RiStarLine style={{ fontSize: 16, color: "#7B2CBF" }} />
          </div>
          <Typography variant="body1">
            No AI health summary available.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Detect if the summary is in Markdown format
  const isMarkdownFormat = isMarkdown(summary);

  // Format content based on detected format
  const formattedContent = isMarkdownFormat
    ? formatMarkdown(summary)
    : `<p>${escapeHtml(summary)}</p>`;

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <div className={classes.iconContainer}>
          <RiStarLine style={{ fontSize: 16, color: "#7B2CBF" }} />
          <BiBrain style={{ fontSize: 24, color: "#7B2CBF" }} />
          <RiStarLine style={{ fontSize: 16, color: "#7B2CBF" }} />
        </div>
        <Typography variant="h6" className={classes.title}>
          AI Health Analysis
        </Typography>
        <Tooltip title="Print summary">
          <IconButton onClick={handlePrint} aria-label="Print AI summary">
            <PrintIcon style={{ color: "#7B2CBF" }} />
          </IconButton>
        </Tooltip>
      </div>
      <div className={classes.disclaimer}>
        <InfoIcon className={classes.disclaimerIcon} />
        <Typography className={classes.disclaimerText}>
          This AI-generated summary is based on the patient's health record data
          from the last 7 days. It should be used as a supplementary tool and
          not as a replacement for professional medical judgment.
        </Typography>
      </div>
      <div
        className={classes.content}
        dangerouslySetInnerHTML={{ __html: formattedContent }}
      />
      <AISummaryPrintable ref={printRef} summary={summary} patientInfo={patientInfo} />
    </Paper>
  );
};

export default AISummary;
